import {
  ISSC_AIR_PATCH_CHAR_UUID,
  ISSC_NOTIFY_CHAR_UUID,
  isIsscNotifyRxCharacteristic,
  isIsscWriteCharacteristic,
  isIsscServiceUuid,
  resolveBleBridgeProfile,
  type BleBridgeProfile,
} from './bleBridgeProfile';
import { IsscCreditGate } from './isscCreditGate';
import { getPrinterBleExtraOptionalServicesFromEnv } from './printerBleUuidConfig';
import { printerDebug, printerDebugWarn } from './printerDebug';

/**
 * Best-effort BLE GATT discovery for ESC/POS thermal printers when the user has not
 * configured VITE_PRINTER_BLE_* (or device overrides). Web Bluetooth still uses UUIDs
 * internally; this module encodes a small allowlist for `optionalServices` and a
 * heuristic to pick a writable characteristic. Vendor-specific; expand only with tested IDs.
 *
 * @see https://developer.mozilla.org/en-US/docs/Web/API/BluetoothRemoteGATTServer/getPrimaryServices
 */

/** Chromium Web Bluetooth: discovery APIs not in all TypeScript `lib.dom` versions. */
type DiscoverableGattServer = BluetoothRemoteGATTServer & {
  getPrimaryServices(): Promise<DiscoverableGattService[]>;
};

type DiscoverableGattService = BluetoothRemoteGATTService & {
  uuid: string;
  getCharacteristics(): Promise<DiscoverableGattCharacteristic[]>;
};

type DiscoverableGattCharacteristic = BluetoothRemoteGATTCharacteristic & {
  uuid: string;
};

/** Minimal shape for unit tests and scoring (Web Bluetooth typings omit `uuid` on some DOM libs). */
export type BleCharacteristicForScoring = {
  uuid: string;
  properties: BluetoothCharacteristicProperties;
};

type WritableCharProps = BluetoothCharacteristicProperties & {
  notify?: boolean;
  indicate?: boolean;
};

/** Primary services to request access to at picker time (Chromium grants GATT per listed service). */
export const BLE_OPTIONAL_SERVICES: string[] = [
  // Nordic UART Service (many BLE-serial bridges; RX char is …0003…)
  '6e400001-b5a3-f393-e0a9-e50e24dcca9e',
  // Common “transparent UART” / printer vendor patterns on 16-bit aliases
  '0000ffe0-0000-1000-8000-00805f9b34fb',
  '0000fff0-0000-1000-8000-00805f9b34fb',
  '0000ff00-0000-1000-8000-00805f9b34fb',
  // Common on portable 58mm-class BLE ESC/POS modules (see e.g. bitbank2/Thermal_Printer ae30/ae01 pair)
  '0000ae30-0000-1000-8000-00805f9b34fb',
  // ISSC transparent UART (some modules)
  '49535343-fe7d-4ae5-8fa9-9fafd205e455',
];

export type PickedBleWriteCharacteristic = {
  characteristic: BluetoothRemoteGATTCharacteristic;
  serviceUuid: string;
  characteristicUuid: string;
  score: number;
  bridgeProfile: BleBridgeProfile;
};

export type BlePickCandidate = {
  serviceUuid: string;
  characteristicUuid: string;
  score: number;
  writeWithoutResponse: boolean;
  write: boolean;
  notify: boolean;
};

function uuidLower(s: string): string {
  return s.trim().toLowerCase();
}

function pushUniqueServiceUuids(out: string[], seen: Set<string>, raw: string): void {
  const k = uuidLower(raw);
  if (!k || seen.has(k)) return;
  seen.add(k);
  out.push(k);
}

/**
 * Chromium only grants GATT access to primary services listed in `requestDevice({ optionalServices })`.
 */
export function mergeBleOptionalServicesForRequest(explicitPrimaryService: string | null | undefined): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  if (explicitPrimaryService?.trim()) {
    pushUniqueServiceUuids(out, seen, explicitPrimaryService);
  }
  for (const u of getPrinterBleExtraOptionalServicesFromEnv()) {
    pushUniqueServiceUuids(out, seen, u);
  }
  for (const u of BLE_OPTIONAL_SERVICES) {
    pushUniqueServiceUuids(out, seen, u);
  }
  return out;
}

/** Service UUID order for probing (built-in list first, then env extras not already listed). */
function bleOptionalServiceProbeOrder(): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const u of BLE_OPTIONAL_SERVICES) {
    pushUniqueServiceUuids(out, seen, u);
  }
  for (const u of getPrinterBleExtraOptionalServicesFromEnv()) {
    pushUniqueServiceUuids(out, seen, u);
  }
  return out;
}

export type TryPickWriteCharacteristicOptions = {
  /**
   * XP-P203A / Nordic UART: never use NUS **TX** (6e400002…) for centrals→printer data.
   */
  penalizeNordicUartTxWrite?: boolean;
  /** Reject characteristics that lack writeWithoutResponse (XP-P203A). */
  requireWriteWithoutResponse?: boolean;
  /** Minimum score for auto-pick; weak write-only channels are skipped. */
  minPickScore?: number;
};

/** BLE characteristic discovery options for Xprinter XP-P203A. */
export function getXpP203aBlePickOptions(): TryPickWriteCharacteristicOptions {
  return {
    penalizeNordicUartTxWrite: true,
    requireWriteWithoutResponse: true,
    minPickScore: 10,
  };
}

/** Nordic UART TX — central writes here often ACK in JS but UART print engine never receives ESC/POS. */
export function isNordicUartTxCharacteristic(serviceUuid: string, characteristicUuid: string): boolean {
  const su = uuidLower(serviceUuid);
  const cu = uuidLower(characteristicUuid);
  if (cu.includes('6e400002')) return true;
  if (su.includes('6e400001') && cu.includes('6e400002')) return true;
  return false;
}

/**
 * Score a writable GATT characteristic for ESC/POS payload delivery (higher = better).
 */
export function scoreBleWriteCharacteristic(
  serviceUuid: string,
  ch: BleCharacteristicForScoring,
  opts?: TryPickWriteCharacteristicOptions,
): number {
  const su = uuidLower(serviceUuid);
  const cu = uuidLower(ch.uuid);
  const p = ch.properties as WritableCharProps;

  if (!p.write && !p.writeWithoutResponse) {
    return -1000;
  }

  let score = 0;

  if (p.writeWithoutResponse) score += 24;
  if (p.write) score += 6;

  // Preferred vendor / NUS RX paths (central → printer)
  if (su.includes('6e400001') && cu.includes('6e400003')) score += 22;
  if (su.includes('ffe0') && cu.includes('ffe1')) score += 16;
  if (su.includes('ae30') && cu.includes('ae01')) score += 16;
  if (su.includes('ff00') && cu.includes('ff02')) score += 16;
  if (su.includes('fff0') && (cu.includes('fff1') || cu.includes('fff2'))) score += 10;

  if (isIsscWriteCharacteristic(su, cu)) score += 20;

  if (isIsscNotifyRxCharacteristic(su, cu)) score -= 100;

  // Notify/indicate-only paths are poor for raw ESC/POS writes
  if ((p.notify || p.indicate) && !p.writeWithoutResponse) score -= 10;
  if (p.notify || p.indicate) score -= 4;

  if (opts?.penalizeNordicUartTxWrite && isNordicUartTxCharacteristic(su, cu)) {
    score -= 100;
  }

  return score;
}

/** Reject characteristics that must not be used for XP-P203A printing. */
export function isAcceptableBleWriteCharacteristic(
  serviceUuid: string,
  ch: BluetoothRemoteGATTCharacteristic,
  opts?: TryPickWriteCharacteristicOptions,
): boolean {
  const score = scoreBleWriteCharacteristic(
    serviceUuid,
    ch as DiscoverableGattCharacteristic,
    opts,
  );
  return score >= 0;
}

/** Prefer Nordic UART RX (central → peripheral) when that service is present. */
function characteristicScore(
  serviceUuid: string,
  ch: DiscoverableGattCharacteristic,
  opts?: TryPickWriteCharacteristicOptions,
): number {
  return scoreBleWriteCharacteristic(serviceUuid, ch, opts);
}

/**
 * Enumerate granted primary services and pick the best writable characteristic for raw writes.
 */
export async function tryPickWriteCharacteristic(
  server: BluetoothRemoteGATTServer,
  opts?: TryPickWriteCharacteristicOptions,
): Promise<PickedBleWriteCharacteristic | null> {
  const ext = server as DiscoverableGattServer;
  let services: DiscoverableGattService[];
  try {
    services = await ext.getPrimaryServices();
  } catch {
    return null;
  }

  const preferredOrder = new Map<string, number>();
  bleOptionalServiceProbeOrder().forEach((u, i) => {
    preferredOrder.set(uuidLower(String(u)), i);
  });

  const sorted = [...services].sort((a, b) => {
    const ia = preferredOrder.has(a.uuid) ? preferredOrder.get(a.uuid)! : 1000;
    const ib = preferredOrder.has(b.uuid) ? preferredOrder.get(b.uuid)! : 1000;
    if (ia !== ib) return ia - ib;
    return a.uuid.localeCompare(b.uuid);
  });

  let best: { ch: DiscoverableGattCharacteristic; serviceUuid: string; score: number } | null = null;
  const candidates: BlePickCandidate[] = [];

  for (const svc of sorted) {
    let chars: DiscoverableGattCharacteristic[];
    try {
      chars = await svc.getCharacteristics();
    } catch {
      continue;
    }
    for (const ch of chars) {
      if (!ch.properties.write && !ch.properties.writeWithoutResponse) continue;
      const p = ch.properties as WritableCharProps;
      const score = characteristicScore(svc.uuid, ch, opts);
      candidates.push({
        serviceUuid: svc.uuid,
        characteristicUuid: ch.uuid,
        score,
        writeWithoutResponse: Boolean(p.writeWithoutResponse),
        write: Boolean(p.write),
        notify: Boolean(p.notify || p.indicate),
      });
      if (opts?.requireWriteWithoutResponse && !ch.properties.writeWithoutResponse) continue;
      const minScore = opts?.minPickScore ?? 0;
      if (score < minScore) continue;
      if (!best || score > best.score) {
        best = { ch, serviceUuid: svc.uuid, score };
      }
    }
  }

  if (candidates.length > 0) {
    const ranked = [...candidates].sort((a, b) => b.score - a.score);
    printerDebug('BLE pick candidates', {
      count: ranked.length,
      top: ranked.slice(0, 8),
    });
  }

  if (!best) return null;

  const bridgeProfile = resolveBleBridgeProfile(best.serviceUuid, best.ch.uuid);

  printerDebug('BLE auto-pick', {
    serviceUuid: best.serviceUuid,
    characteristicUuid: best.ch.uuid,
    score: best.score,
    bridgeProfile,
    writeWithoutResponse: best.ch.properties.writeWithoutResponse,
    write: best.ch.properties.write,
  });

  return {
    characteristic: best.ch,
    serviceUuid: best.serviceUuid,
    characteristicUuid: best.ch.uuid,
    score: best.score,
    bridgeProfile,
  };
}

/**
 * Resolve explicit env/manual UUIDs; fall back to auto-pick when TX or low score (XP-P203A).
 */
export async function resolveBleWriteCharacteristic(
  server: BluetoothRemoteGATTServer,
  uuids: { service: string; writeChar: string } | null,
  opts?: TryPickWriteCharacteristicOptions,
): Promise<PickedBleWriteCharacteristic & { explicitUuidFallback?: boolean }> {
  let explicitUuidFallback = false;
  if (uuids) {
    try {
      const svc = await server.getPrimaryService(uuids.service);
      const ch = await svc.getCharacteristic(uuids.writeChar);
      const score = scoreBleWriteCharacteristic(
        uuids.service,
        { uuid: (ch as DiscoverableGattCharacteristic).uuid, properties: ch.properties },
        opts,
      );
      const canWnr = Boolean(ch.properties.writeWithoutResponse);
      if (
        isAcceptableBleWriteCharacteristic(uuids.service, ch, opts) &&
        (!opts?.requireWriteWithoutResponse || canWnr) &&
        score >= (opts?.minPickScore ?? 0)
      ) {
        printerDebug('BLE explicit UUID', {
          serviceUuid: uuids.service,
          characteristicUuid: uuids.writeChar,
          score,
          writeWithoutResponse: canWnr,
          write: Boolean(ch.properties.write),
        });
        const bridgeProfile = resolveBleBridgeProfile(
          uuids.service,
          (ch as DiscoverableGattCharacteristic).uuid,
        );
        return {
          characteristic: ch,
          serviceUuid: uuids.service,
          characteristicUuid: (ch as DiscoverableGattCharacteristic).uuid,
          score,
          bridgeProfile,
        };
      }
      explicitUuidFallback = true;
      printerDebugWarn('BLE explicit UUID rejected (wrong channel e.g. NUS TX); auto-picking', {
        serviceUuid: uuids.service,
        characteristicUuid: uuids.writeChar,
        score,
        writeWithoutResponse: canWnr,
        write: Boolean(ch.properties.write),
      });
    } catch {
      explicitUuidFallback = true;
      printerDebugWarn('BLE explicit UUID getCharacteristic failed; auto-picking');
    }
  }

  const picked = await tryPickWriteCharacteristic(server, opts);
  if (!picked) {
    throw new Error('bluetoothPrinter.errors.bleAutoDiscoveryFailed');
  }
  return { ...picked, explicitUuidFallback: explicitUuidFallback || undefined };
}

/**
 * ISSC Air Patch: notify + optional open byte (some Printer001-* modules).
 */
export async function tryEnableIsscAirPatch(
  server: BluetoothRemoteGATTServer,
  serviceUuid: string,
): Promise<{ enabled: boolean; error?: string }> {
  if (!isIsscServiceUuid(serviceUuid)) {
    return { enabled: false };
  }
  try {
    const svc = await server.getPrimaryService(serviceUuid);
    const ch = await svc.getCharacteristic(ISSC_AIR_PATCH_CHAR_UUID);
    const p = ch.properties as WritableCharProps;
    if (p.notify || p.indicate) {
      const notifyCh = ch as BluetoothRemoteGATTCharacteristic & {
        startNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
      };
      await notifyCh.startNotifications();
      printerDebug('ISSC air patch notify enabled', {
        serviceUuid,
        characteristicUuid: ISSC_AIR_PATCH_CHAR_UUID,
      });
    }
    const open = new Uint8Array([0x01]);
    const wwr = (ch as unknown as { writeValueWithoutResponse?: (v: BufferSource) => Promise<void> })
      .writeValueWithoutResponse;
    if (p.writeWithoutResponse && typeof wwr === 'function') {
      await wwr.call(ch, open);
      printerDebug('ISSC air patch open (WNR)', { byte: 0x01 });
    } else if (p.write) {
      await ch.writeValue(open);
      printerDebug('ISSC air patch open (write)', { byte: 0x01 });
    }
    return { enabled: true };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    printerDebugWarn('ISSC air patch enable failed', { serviceUuid, error: msg });
    return { enabled: false, error: msg };
  }
}

/**
 * Enable ISSC Transparent TX notifications so the UART bridge forwards host writes (some firmware).
 */
export async function tryEnableIsscNotifyHandshake(
  server: BluetoothRemoteGATTServer,
  serviceUuid: string,
): Promise<{
  enabled: boolean;
  characteristicUuid?: string;
  error?: string;
  creditGate?: IsscCreditGate;
  notifyCharacteristic?: BluetoothRemoteGATTCharacteristic;
}> {
  if (!isIsscServiceUuid(serviceUuid)) {
    return { enabled: false };
  }
  try {
    const svc = await server.getPrimaryService(serviceUuid);
    const ch = await svc.getCharacteristic(ISSC_NOTIFY_CHAR_UUID);
    const p = ch.properties as WritableCharProps;
    if (!p.notify && !p.indicate) {
      return { enabled: false, error: 'no notify property' };
    }
    const notifyCh = ch as BluetoothRemoteGATTCharacteristic & {
      startNotifications(): Promise<BluetoothRemoteGATTCharacteristic>;
    };
    await notifyCh.startNotifications();
    const creditGate = new IsscCreditGate();
    const onNotify = (ev: Event): void => {
      const target = (ev.target as BluetoothRemoteGATTCharacteristic | null) ?? ch;
      const v = (target as BluetoothRemoteGATTCharacteristic & { value?: DataView }).value;
      if (v) creditGate.onNotifyValue(v);
    };
    ch.addEventListener('characteristicvaluechanged', onNotify);
    printerDebug('ISSC notify enabled', {
      serviceUuid,
      characteristicUuid: ISSC_NOTIFY_CHAR_UUID,
    });
    return {
      enabled: true,
      characteristicUuid: ISSC_NOTIFY_CHAR_UUID,
      creditGate,
      notifyCharacteristic: ch,
    };
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    printerDebugWarn('ISSC notify enable failed', { serviceUuid, error: msg });
    return { enabled: false, error: msg };
  }
}
